import {EventEmitter, Injectable} from "@angular/core";
import { Observable, throwError } from "rxjs";
import {
  FileSystemFolderAndDocument,
  FileSystemFolderDetails,
  FileSystemService
} from "../../../../../swaggerServices";
import { TenantConfigService } from "../../../../shared/services/tenant-config.service";
import { ConfigStateService } from "../../../config/config-state.service";

interface IPropertyVisibility {
  sortBy: boolean;
  fileType: boolean;
  layout: boolean;
  selectAll: boolean;
  newFolder: boolean;
  uploadDocument: boolean;
  downloadDocument: boolean;
}

interface IFolderPathNode {
  id: string;
  name: string;
}

@Injectable()
export class RubixDocumentsService {
  rootFolderId: string;
  rootFolderName: string;
  rootFolderLink: string;
  currentFolderDetails: FileSystemFolderDetails;
  currentFolderPath: IFolderPathNode[] = [];
  previousFolderDetails: FileSystemFolderDetails[] = [];
  nextFolderDetails: FileSystemFolderDetails[] = [];
  loading = false;
  taxReturnId = null;
  client = null;
  uiConfig: TenantConfigService;
  taxReturnAlias: string;
  sortBy: 'type' | 'update' = 'type';
  sortOrder: 'asc' | 'desc' = 'desc';
  selectedChildren: FileSystemFolderAndDocument[] = [];
  propertyVisibility:IPropertyVisibility = {
    sortBy: true,
    fileType: true,
    layout: true,
    selectAll: true,
    newFolder: true,
    uploadDocument: true,
    downloadDocument: true,
  };
  fileStoreType: 'rubixDocuments'|'configExports' = "rubixDocuments";

  notifyNavigationChange$ = new EventEmitter<boolean>();
  notifyFolderViewChange$ = new EventEmitter<boolean>();
  notifyPropertyVisibilityChange$ = new EventEmitter<IPropertyVisibility>();

  constructor(
    private fileSystemService: FileSystemService,
    private config: ConfigStateService,
  ) {
  }

  private emitStateChange() {
    this.notifyNavigationChange$.emit(true);
    this.notifyFolderViewChange$.emit(true);
  }

  private setLoading(loading: boolean) {
    this.loading = loading;
    this.emitStateChange();
  }

  private resetRootNavigationState() {
    this.selectedChildren = [];
    this.previousFolderDetails = [];
    this.nextFolderDetails = [];
  }

  private setRootFolderDetails(response: FileSystemFolderDetails) {
    this.rootFolderId = response.folderMetaData.id;
    this.rootFolderName = response.folderMetaData.name;
    this.currentFolderDetails = response;
    this.currentFolderPath = [{ id: this.rootFolderId, name: this.rootFolderName }];
  }

  private updateCurrentPathForFolder(folderId: string, folderName: string) {
    const existingIndex = this.currentFolderPath.findIndex(pathNode => pathNode.id === folderId);
    if (existingIndex >= 0) {
      this.currentFolderPath = this.currentFolderPath.slice(0, existingIndex + 1);
      return;
    }

    this.currentFolderPath = [
      ...this.currentFolderPath,
      { id: folderId, name: folderName },
    ];
  }

  private applyFolderNavigationResponse(response: FileSystemFolderDetails, folderId: string, folderName?: string) {
    this.nextFolderDetails = [];
    this.selectedChildren = [];
    this.previousFolderDetails.push(this.currentFolderDetails);
    this.currentFolderDetails = response;
    this.updateCurrentPathForFolder(folderId, folderName || response.folderMetaData.name);
    this.sortChildren();
    this.stopLoading();
  }

  private getFolderDetailsRequest(folderId: string): Observable<FileSystemFolderDetails> {
    return this.fileStoreType === "configExports"
      ? this.fileSystemService.getConfigFolderDetail({ baseFolder: false, folderId })
      : this.fileSystemService.fileSystemGetFolderDetailPut({ folderId });
  }

  private prepareFolderLink(iFolderLink) {
    const [protocol, path] = iFolderLink.split("://");
    return {
      protocol,
      folderLink: path,
    };
  }

  setTaxReturnForImageUploads(taxReturn: any) {
    this.taxReturnId = taxReturn?.id || null;
  }

  setClientForClientFileQuestionUploads(client: any) {
    this.client = client?.id || null;
  }

  getRootFolderByFolderLink(iFolderLink: string) {
    const { protocol, folderLink } = this.prepareFolderLink(iFolderLink);
    this.rootFolderLink = iFolderLink;
    this.resetRootNavigationState();
    this.setLoading(true);
    this.fileSystemService.fileSystemGetFolderDetailFromLinkPut({
      folderLink,
      protocol,
    }).subscribe(
      (response: FileSystemFolderDetails) => {
        this.setRootFolderDetails(response);
        this.sortChildren();
        this.stopLoading();
      },
      (error) => {
        this.stopLoading();
      }
    );
  }

  setPropertyVisibility(propertyVisibility){
    this.propertyVisibility = propertyVisibility;
  }

  setFileStoreType(fileStoreType){
    this.fileStoreType = fileStoreType;
  }

  getRootConfigFolderDetails({baseFolder, folderId}: {baseFolder: boolean, folderId: string}) {
    this.rootFolderLink = null;
    this.resetRootNavigationState();
    this.setLoading(true);
    this.fileSystemService.getConfigFolderDetail({
      baseFolder,
      folderId,
    }).subscribe(
      (response: FileSystemFolderDetails) => {
        this.setRootFolderDetails(response);
        this.sortChildren();
        this.stopLoading();
      },
      (error) => {
        this.stopLoading();
      }
    );
  }

  checkUnderRootFolder(folderId: string) {
    if (!folderId) {
      return false;
    }

    if (folderId === this.rootFolderId) {
      return true;
    }

    if (this.currentFolderPath.some(pathNode => pathNode.id === folderId)) {
      return true;
    }

    return this.currentFolderDetails?.folderChildren?.some(
      child => child.wstype === "folder" && child.id === folderId
    ) || false;
  }

  private syncCurrentPathToCurrentFolder() {
    const currentFolderId = this.currentFolderDetails?.folderMetaData?.id;
    if (!currentFolderId) {
      return;
    }

    const existingIndex = this.currentFolderPath.findIndex(pathNode => pathNode.id === currentFolderId);
    if (existingIndex >= 0) {
      this.currentFolderPath = this.currentFolderPath.slice(0, existingIndex + 1);
      return;
    }

    if (currentFolderId === this.rootFolderId) {
      this.currentFolderPath = [{ id: this.rootFolderId, name: this.rootFolderName || this.currentFolderDetails.folderMetaData.name }];
      return;
    }

    this.currentFolderPath = [
      { id: this.rootFolderId, name: this.rootFolderName || this.currentFolderDetails.folderMetaData.name },
      { id: currentFolderId, name: this.currentFolderDetails.folderMetaData.name },
    ];
  }

  toPreviousFolder() {
    if (this.previousFolderDetails.length > 0) {
      this.nextFolderDetails.push(this.currentFolderDetails);
      this.currentFolderDetails = this.previousFolderDetails.pop();
      this.syncCurrentPathToCurrentFolder();
      this.emitStateChange();
    }
  }

  toNextFolder() {
    if (this.nextFolderDetails.length > 0) {
      this.previousFolderDetails.push(this.currentFolderDetails);
      this.currentFolderDetails = this.nextFolderDetails.pop();
      this.syncCurrentPathToCurrentFolder();
      this.emitStateChange();
    }
  }

  toFolder(folderId: string, folderName?: string) {
    if (this.checkUnderRootFolder(folderId)) {
      this.setLoading(true);
      this.getFolderDetailsRequest(folderId).subscribe(
        (response: FileSystemFolderDetails) => {
          this.applyFolderNavigationResponse(response, folderId, folderName);
        },
        (error) => {
          this.stopLoading();
        }
      );
    }
  }

  createFolder(folderName: string) {
    this.setLoading(true);
    this.fileSystemService.fileSystemCreateFolderPut({folderName: folderName, folderId: this.currentFolderDetails.folderMetaData.id}).subscribe(
      (response: FileSystemFolderDetails) => {
        this.nextFolderDetails = [];
        this.previousFolderDetails = [];
        this.currentFolderDetails = response;
        this.sortChildren();
        this.stopLoading();
      },
      (error) => {
        this.stopLoading();
      }
    );
  }

  uploadFile(file: Blob) {
    this.setLoading(true);
    this.fileSystemService.fileSystemUploadDocumentPost(
        file,
        this.currentFolderDetails.folderMetaData.id
      ).subscribe(
      (response: FileSystemFolderDetails) => {
        this.nextFolderDetails = [];
        this.previousFolderDetails = [];
        this.currentFolderDetails = response;
        this.sortChildren();
        this.stopLoading();
      },
      (error) => {
        this.stopLoading();
      }
    );
  }

  // uploadImage(file: Blob) {
  //   this.uiConfig = new TenantConfigService(this.config);
  //   this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
  //   if (!this.taxReturnId) {
  //     return throwError(`Expected ${this.taxReturnAlias} to be set`);
  //   }
  //   return this.fileSystemService.fileSystemUploadImagePost(file, this.taxReturnId);
  // }

  uploadFileQuestion(file: Blob, questionId: number) {
    this.uiConfig = new TenantConfigService(this.config);
    this.taxReturnAlias = this.uiConfig.config.taxReturnAlias;
    if (!this.taxReturnId && this.client) {
      return this.fileSystemService.fileSystemUploadFileQuestionPost(file, questionId, this.taxReturnId, this.client);
    }else {
      if (!this.taxReturnId) {
        return throwError(`Expected ${this.taxReturnAlias} to be set`);
      }
      return this.fileSystemService.fileSystemUploadFileQuestionPost(file, questionId, this.taxReturnId);
    }
  }

  // downloadImage(imageId: string, { width = null, height = null } : { width?: number, height?: number}) {
  //   return this.fileSystemService.fileSystemDownloadImagePut({ taxReturnId: this.taxReturnId, imageId, width, height });
  // }

  downloadFileQuestion(fileFolderId: string) {
    return this.fileSystemService.fileSystemDownloadSingleFilePut({ documentId: fileFolderId });
  }

  stopLoading() {
    this.setLoading(false);
  }

  toggleSelectChild(child: FileSystemFolderAndDocument) {
    if (this.isChildSelected(child)) {
      this.selectedChildren.splice(this.selectedChildren.indexOf(child), 1);
    } else {
      this.selectedChildren.push(child);
    }
  }

  toggleSelectAllChildren() {
    if (this.isAllChildrenSelected()) {
      this.selectedChildren = [];
    } else {
      this.selectedChildren = [...this.currentFolderDetails.folderChildren];
    }
  }

  isAllChildrenSelected() {
    return this.selectedChildren.length === this.currentFolderDetails.folderChildren.length;
  }

  isChildSelected(child: FileSystemFolderAndDocument) {
    return this.selectedChildren.indexOf(child) > -1;
  }

  hasAnyChildSelected() {
    return this.selectedChildren.length > 0;
  }

  downloadSelected(fileNameHint) {
    const documentIds = this.selectedChildren.filter(c => c.wstype === "document").map(c => c.id);
    const folderIds = this.selectedChildren.filter(c => c.wstype === "folder").map(c => c.id);

    if (documentIds.length === 1 && folderIds.length === 0) {
      return this.fileStoreType === "configExports" ? this.fileSystemService.downloadSingleConfigFile({ documentId: documentIds[0] }) : this.fileSystemService.fileSystemDownloadSingleFilePut({ documentId: documentIds[0] });
    }

    return this.fileStoreType === "configExports" ? this.fileSystemService.downloadManyConfigFiles({ folderIds, documentIds, fileNameHint }):this.fileSystemService.fileSystemDownloadManyFilesPut({ folderIds, documentIds, fileNameHint });
  }

  changeSortBy(sortBy: 'type' | 'update') {
    this.sortBy = sortBy;
    this.sortChildren();
    this.notifyFolderViewChange$.emit(true);
  }

  changeSortOrder(sortOrder: 'asc' | 'desc') {
    this.sortOrder = sortOrder;
    this.sortChildren();
    this.notifyFolderViewChange$.emit(true);
  }

  sortChildren() {
    this.currentFolderDetails.folderChildren.sort((a, b) => {
      // folders always appear first
      if (a.wstype === 'folder' && b.wstype !== 'folder') return -1;
      if (a.wstype !== 'folder' && b.wstype === 'folder') return 1;
      if (this.sortBy === 'type') {
        if (a.wstype === b.wstype) {
          if (a.extension?.toUpperCase() === b.extension?.toUpperCase()) {
            // sort alphabetically by name within the extension group
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
          }
          if (a.extension?.toUpperCase() < b.extension?.toUpperCase()) return this.sortOrder === 'asc' ? -1 : 1;
          if (a.extension?.toUpperCase() > b.extension?.toUpperCase()) return this.sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
      }
      if (this.sortBy === 'update') {
        if (a.wstype === b.wstype) {
          if (a.edit_date === b.edit_date) {
            // sort alphabetically by name within the extension group
            if (a.name < b.name) return -1;
            if (a.name > b.name) return 1;
          }
          if (a.edit_date < b.edit_date) return this.sortOrder === 'asc' ? -1 : 1;
          if (a.edit_date > b.edit_date) return this.sortOrder === 'asc' ? 1 : -1;
        }
        return 0;
      }
    });
  }

}
